<script setup lang="ts">
import { $dayjs } from '@/utils/dayjs'

import { weeklyPriceRangeText } from '~/helpers/priceHelper'
import { hasOwnProperty } from '~/helpers/objectHelper'

import type {
  PayloadTracking,
  Period,
  PeriodDurationInquiry,
  SelectedPeriod,
} from '~/types/inquiry/types'
import type { MetaAncestries } from '~/types/types'
import type { HouseDetails } from '~/types/house/types'
import type { Locale } from 'lc-services/types'
import type BaseCalendar from './BaseForm/components/BaseCalendar.vue'

const { t, locale } = useI18n<unknown, Locale>()
const localePath = useLocalePath()
const route = useRoute()
const { trackEvent } = useTracking()
const { isMobile } = useBreakpoint()

const { currency } = useUser()
const {
  booking,
  bookedDates,
  bookedDatesExceptChecks,
  fetchAndsetSelectedPeriod,
  fetchBookings,
  setNextStep,
  setDates,
  setPrice,
} = useHouseInquiryBooking()
const { setPreOrderAndCookies } = useHousePreOrder()
const { startTimeWeek, endTimeWeek, saturdayEndTimeWeek } =
  useCallCenterSchedule()

$dayjs.locale(locale.value)

const ancestries = route.meta?.ancestries as MetaAncestries | undefined
const privateToken = computed(
  () => route.query['private-token']?.toString() ?? '',
)

const props = withDefaults(
  defineProps<{
    bookingSent?: boolean
    house: HouseDetails
    payloadTracking: PayloadTracking
    periods: PeriodDurationInquiry[]
    isBooking?: boolean
    showCalendar?: boolean
  }>(),
  {
    bookingSent: false,
    isBooking: false,
    showCalendar: false,
  },
)

const emits = defineEmits<{
  'close-inquiry': []
  'hide-calendar': []
  loading: [value: boolean]
}>()

const baseCalendarRef = ref<InstanceType<typeof BaseCalendar> | null>(null)
const nightCount = ref(0)
const pricesToDisplay = ref('')
const selectedPeriod = ref<SelectedPeriod | null>(null)
const startDate = ref(
  $dayjs(booking.value.startDate ?? undefined)
    .startOf('month')
    .format('YYYY-MM-DD'),
)
const endDate = ref(
  $dayjs(booking.value.endDate ?? startDate.value)
    .endOf('month')
    .add(
      // 1 month less if there is a booking endDate
      (isMobile.value ? 7 : 2) - Number(Boolean(booking.value.endDate)),
      'month',
    )
    .format('YYYY-MM-DD'),
)

const allMinimumDurationValid = computed(
  () => selectedPeriod.value?.allMinimumDurationValid ?? false,
)
const fullyCovered = computed(() => selectedPeriod.value?.fullyCovered ?? null)
const minimumPriceWithoutOpCosts = computed(
  () => selectedPeriod.value?.minimumPriceWithoutOpCosts ?? 0,
)

const currentTimezone = computed(() => {
  const timezoneClusterOrCountry =
    props.house?.destination?.cluster?.timezone ||
    props.house?.destination?.country?.timezone

  return props.house.destination?.timezone || timezoneClusterOrCountry
})

const hasSelectedPeriod = computed(
  () => !!selectedPeriod.value && Object.keys(selectedPeriod.value)?.length > 0,
)

const periodBookableOnline = computed(() => {
  if (
    booking.value.startDate &&
    booking.value.endDate &&
    props.house.onlineReservation
  ) {
    return selectedPeriod.value?.periodBookableOnline || false
  }

  return true
})

const dateIsIncorrectPeriod = computed(() => {
  if (fullyCovered.value && !allMinimumDurationValid.value) {
    return true
  }

  return false
})

const isPeriodInvalidAndPartial = computed(() => {
  if (
    !fullyCovered.value &&
    !allMinimumDurationValid.value &&
    selectedPeriod.value &&
    selectedPeriod.value?.periods?.length > 0
  ) {
    const hasInvalidPeriod = selectedPeriod.value.periods.some(
      (period) => !period.validMinimumDuration,
    )

    return hasInvalidPeriod
  }

  return false
})

const showPublicTaxesInfo = computed(() => {
  if (
    props.house.publicTaxes &&
    defaultPrice.value !== t('house.price_upon_request')
  ) {
    if (showTotalPrice.value) {
      if (!booking.value.price.value) return true

      return false
    } else {
      return true
    }
  }

  return false
})

const blockedCalendar = computed(() => {
  if (dateIsIncorrectPeriod.value || isPeriodInvalidAndPartial.value) {
    return true
  }

  return false
})

const showTotalPrice = computed(() => {
  return Boolean(
    booking.value.startDate &&
      booking.value.endDate &&
      nightCount.value > 0 &&
      !dateIsIncorrectPeriod.value &&
      !isPeriodInvalidAndPartial.value &&
      selectedPeriod.value &&
      hasOwnProperty(selectedPeriod.value, 'publicPrice') &&
      hasOwnProperty(selectedPeriod.value, 'validPrice'),
  )
})

const wordingDetailDateIsInPeriod = computed(() => {
  const selectedPeriods = selectedPeriod.value?.periods

  if (
    selectedPeriods &&
    (dateIsIncorrectPeriod.value || isPeriodInvalidAndPartial.value)
  ) {
    if (selectedPeriods?.length === 1) {
      return setErrorMessage(selectedPeriods[0])
    } else if (selectedPeriods?.length > 1) {
      const weeklyPeriods = selectedPeriods.filter((p) =>
        p.periodType.includes('weekly'),
      )
      const nightlyPeriods = selectedPeriods.filter((p) =>
        p.periodType.includes('nightly'),
      )

      if (nightlyPeriods?.length === selectedPeriods?.length) {
        return getMaxConstraintPeriod(selectedPeriods)
      } else if (weeklyPeriods) {
        return getMaxConstraintPeriod(weeklyPeriods)
      }

      return {
        day: null,
        path: `detail_minimum_stay_nightly`,
        number: 1,
      }
    }
  }

  return null
})

const isRequiringShortStayWarning = computed(() => {
  return nightCount.value > 0 && nightCount.value < 3
})

const defaultPrice = computed(() =>
  weeklyPriceRangeText(
    { t, locale },
    props.house.minPrice[currency.value],
    props.house.maxPrice[currency.value],
    currency.value,
    Boolean(props.house.displayPrices),
  ),
)

const mobileClosed = () => {
  if (!isModifying.value || !booking.value.endDate) emits('close-inquiry')
}

const getMaxConstraintPeriod = (periods: Period[]) => {
  const maxDuration = Math.max(...periods.map((p) => p.minimumDuration))
  const period = periods.find((p) => p.minimumDuration === maxDuration)

  return period ? setErrorMessage(period) : null
}

const setErrorMessage = (period: Period) => {
  const periodType = period.periodType === 'nightly' ? 'nightly' : 'weekly'

  return {
    day: period.periodType.replace('weekly_by_', ''),
    path: `detail_minimum_stay_${periodType}`,
    number: period.minimumDuration,
  }
}

const backTo = (callback: () => void) => {
  callback()
}

const fetchHouseBookings = () =>
  fetchBookings(props.house.id, {
    end_date: endDate.value,
    private_token: privateToken.value,
    start_date: startDate.value,
  })

const handleCloseDatePicker = (callback: () => void) => {
  callback()
}

const renderPreviousMonth = async () => {
  startDate.value = $dayjs(startDate.value, 'YYYY-MM-DD')
    .subtract(1, 'month')
    .format('YYYY-MM-DD')

  if (props.house.displayAvailabilities) await fetchHouseBookings()
}

const renderNextMonth = async () => {
  startDate.value = $dayjs(endDate.value, 'YYYY-MM-DD').format('YYYY-MM-DD')
  endDate.value = $dayjs(startDate.value, 'YYYY-MM-DD')
    .add(
      // 1 month less if there is a booking endDate
      (isMobile.value ? 7 : 2) - Number(Boolean(booking.value.endDate)),
      'month',
    )
    .format('YYYY-MM-DD')

  if (props.house.displayAvailabilities) await fetchHouseBookings()
}

const handleBlockedCalendar = () => {
  if (blockedCalendar.value) {
    datesCleared()
  }
}

const setSelectedPeriod = async (customCurrency = currency.value) => {
  nightCount.value = 0

  if (booking.value.endDate) {
    const start = $dayjs(booking.value.startDate, 'YYYY-MM-DD')
    const end = $dayjs(booking.value.endDate, 'YYYY-MM-DD')
    nightCount.value = end.diff(start, 'days')

    emits('loading', true)
    selectedPeriod.value = await fetchAndsetSelectedPeriod({
      currency: customCurrency ?? currency.value,
      displayAvailabilities: Boolean(props.house.displayAvailabilities),
      displayPrices: Boolean(props.house.displayPrices),
      houseId: Number(props.house.id),
      minPrice: props.house.minPrice,
      maxPrice: props.house.maxPrice,
    })

    if (booking.value.price.isValid) {
      pricesToDisplay.value = booking.value.price.text
    } else {
      pricesToDisplay.value = defaultPrice.value
    }

    emits('loading', false)

    if (props.isBooking && periodBookableOnline.value) {
      await sendPrebooking()
      emits('close-inquiry')
    }

    // in case of concurrent asynchronous calls
    if (!booking.value.endDate) {
      resetData(customCurrency)
    }
  } else {
    resetData(customCurrency)
  }
}

const resetData = (customCurrency = currency.value) => {
  selectedPeriod.value = null
  pricesToDisplay.value = defaultPrice.value

  setPrice({
    isValid: false,
    text: defaultPrice.value,
    price: props.house.minPrice[customCurrency],
    definitivePrice: false,
  })
}

const datesCleared = async () => {
  setDates(null, null)
  await setSelectedPeriod()

  openCalendar()
}

const handleSetDatesStart = async (date: Date | null) => {
  if (date) {
    const startDate = $dayjs(date).format('YYYY-MM-DD')
    setDates(startDate, null)
  }
}

const handleSetDatesEnd = async (date: Date | null) => {
  if (date) {
    const startDate = $dayjs(booking.value.startDate).format('YYYY-MM-DD')
    const endDate = $dayjs(date).format('YYYY-MM-DD')
    setDates(startDate, endDate)

    emits('hide-calendar')
    await setSelectedPeriod()

    trackEvent({
      category: 'Product page',
      event: 'date_selected',
      product: {
        ...props.payloadTracking,
        price: booking.value.price.value || undefined,
      },
    })
  }
}

const handlePreOrderRedirect = async () => {
  const queryID = route.query?.queryID
  const query = queryID ? { queryID } : {}

  navigateTo(
    localePath({
      name: 'house-preorder',
      params: { slug: route.params.slug },
      query,
    }),
  )
}

const sendPrebooking = async () => {
  setPreOrderAndCookies({
    destinationId: props.house.destinationId,
    endDate: booking.value.endDate || '',
    nightCount: nightCount.value.toString(),
    pid: props.house.id,
    price: booking.value.price.value?.toString() || '',
    startDate: booking.value.startDate || '',
  })

  trackEvent({
    category: 'Product page',
    event: 'product_option_started',
    product: {
      ...props.payloadTracking,
      price: booking.value.price.value || undefined,
    },
    label: {
      destid: props.house.destinationId,
      endDate: (booking.value.endDate ?? '').toString(),
      pid: props.house.id,
      price: (booking.value.price.value || '').toString(),
      startDate: (booking.value.startDate ?? '').toString(),
      stayLength: (nightCount.value ?? '').toString(),
    },
  })

  await handlePreOrderRedirect()
}

const submitDatePicker = async (e?: Event, preBooking = false) => {
  if (booking.value.endDate) {
    if (preBooking && periodBookableOnline.value) {
      await sendPrebooking().catch(console.error)

      emits('close-inquiry')
    } else {
      trackEvent({
        category: 'Product page',
        cluster_name: ancestries?.clusterName,
        country_name: ancestries?.countryName,
        destination_name: ancestries?.destinationName,
        event: 'product_inquiry_started',
        product: {
          ...props.payloadTracking,
          price: booking.value.price.value || undefined,
        },
        label: {
          destid: props.house.destinationId,
          endDate: booking.value.endDate,
          pid: props.house.id,
          price: (booking.value.price.value || '').toString(),
          startDate: (booking.value.startDate ?? '').toString(),
          stayLength: (nightCount.value ?? '').toString(),
        },
      })

      setNextStep()
    }
  } else {
    e?.stopPropagation()
    openCalendar()
  }
}

const isModifying = ref(false)
const modifyDates = () => {
  isModifying.value = true

  setTimeout(() => {
    openCalendar()
  }, 100)
}

const trackHeaderPhone = () => {
  trackEvent({
    event: 'phone_number_click',
    product: {
      ...props.payloadTracking,
      price: booking.value.price.value || props.payloadTracking.price,
    },
    label: { section: 'inquiry' },
    source: 'inquiry',
  })
}

const openCalendar = () => {
  baseCalendarRef.value?.openCalendar()
}

onMounted(async () => {
  await Promise.all(
    [setSelectedPeriod(currency.value)].concat(
      props.house.displayAvailabilities ? fetchHouseBookings() : [],
    ),
  )
})

watch(currency, async (value) => {
  await setSelectedPeriod(value)
})

watch(baseCalendarRef, (value) => {
  if (value && props.showCalendar) openCalendar()
})
</script>

<template>
  <div class="pb-10 md:pb-0">
    <InquiryMakeRequestSummaryHeader
      :has-selected-period="hasSelectedPeriod"
      :house="house"
      :prices-to-display="pricesToDisplay"
      :show-public-taxes-info="showPublicTaxesInfo"
      @handle-modify-dates="modifyDates"
    />

    <div class="inquiry-date-picker__calendar">
      <label>{{ $t('house.dates') }}</label>

      <BaseFormComponentsBaseCalendar
        ref="baseCalendarRef"
        :booked-dates="bookedDates.flat()"
        :end-date="booking.endDate"
        :has-footer="!isMobile"
        :has-header="isMobile"
        :is-affixed="isMobile"
        :period-dates="periods"
        :placeholder="{
          checkIn: $t('calendar.check_in'),
          checkOut: $t('calendar.check_out'),
        }"
        :rules="`calendarRequired|datesAlreadyBooked:${bookedDatesExceptChecks}`"
        :show-input-calendar="!isMobile"
        :start-date="booking.startDate"
        :timezone="currentTimezone"
        data-cy="inquiry-date-picker__base-calendar"
        data-testid="inquiry-date-picker__base-calendar"
        disabled-days-before-day-date
        name="search-calendar"
        position="right"
        validate-on-mount
        wrapper-class="inquiry-date-picker__base-calendar"
        @clear-dates="datesCleared"
        @close-date-picker="mobileClosed"
        @render-next-date="renderNextMonth"
        @render-previous-date="renderPreviousMonth"
        @update:end-date="handleSetDatesEnd"
        @update:start-date="handleSetDatesStart"
        @click.stop="handleBlockedCalendar"
      >
        <template #lc-calendar-header-mobile="{ clearDates, closeDatePicker }">
          <div class="mb-4 flex justify-between">
            <button
              class="flex items-center"
              type="button"
              @click="backTo(closeDatePicker)"
            >
              <BaseIcon class="mr-2" name="navArrowLeft" />
              <div class="text-md font-medium">
                {{ $t('global.go_back') }}
              </div>
            </button>
            <button
              :class="[
                'text-md font-bold underline',
                { 'text-gray-400': !booking.startDate && !booking.endDate },
                { 'text-gray-700': booking.startDate || booking.endDate },
              ]"
              type="button"
              @click="clearDates"
            >
              {{ $t('inquiry.clearDates') }}
            </button>
          </div>
        </template>

        <template #lc-calendar-footer="{ clearDates, closeDatePicker }">
          <div class="mt-4 flex justify-end">
            <button
              :class="[
                'mr-10 text-md font-bold underline',
                {
                  'cursor-not-allowed text-gray-400':
                    !booking.startDate && !booking.endDate,
                },
                { 'text-gray-700': booking.startDate || booking.endDate },
              ]"
              :disabled="!booking.startDate && !booking.endDate"
              type="button"
              @click="clearDates"
            >
              {{ $t('inquiry.clearDates') }}
            </button>
            <button
              class="texy-primary-400 text-md font-bold"
              type="button"
              @click="handleCloseDatePicker(closeDatePicker)"
            >
              {{ $t('global.close') }}
            </button>
          </div>
        </template>
      </BaseFormComponentsBaseCalendar>

      <p
        v-if="isRequiringShortStayWarning && !dateIsIncorrectPeriod"
        class="mb-0 mt-1 text-sm text-error"
        data-testid="inquiry-date-picker__minimum-stay"
      >
        {{ $t('house.minimum_stay_warning') }}
      </p>

      <i18n-t
        v-if="Boolean(wordingDetailDateIsInPeriod)"
        class="mb-0 mt-1 text-sm text-error"
        data-testid="inquiry-date-picker__incorrect-period"
        :keypath="`house.${wordingDetailDateIsInPeriod?.path}`"
        tag="p"
      >
        <template
          v-if="
            wordingDetailDateIsInPeriod?.path === 'detail_minimum_stay_nightly'
          "
          #nights
        >
          {{ $t(`house.nights`, wordingDetailDateIsInPeriod.number) }}
        </template>
        <template
          v-if="
            wordingDetailDateIsInPeriod?.path !==
              'detail_minimum_stay_nightly' &&
            wordingDetailDateIsInPeriod?.path !==
              'selected_dates_are_incorrect' &&
            wordingDetailDateIsInPeriod?.day
          "
          #day
        >
          <strong>{{ $t(`house.${wordingDetailDateIsInPeriod.day}`) }}</strong>
        </template>
        <template
          v-if="
            wordingDetailDateIsInPeriod &&
            wordingDetailDateIsInPeriod.path !==
              'detail_minimum_stay_nightly' &&
            wordingDetailDateIsInPeriod.path !== 'selected_dates_are_incorrect'
          "
          #weeks
        >
          {{ $t(`house.weeks`, wordingDetailDateIsInPeriod.number) }}
        </template>
      </i18n-t>
    </div>

    <InquiryBlockTotal
      v-if="showTotalPrice"
      :currency="currency"
      :house="house"
      :is-desktop="!isMobile"
      :minimum-price-without-op-costs="minimumPriceWithoutOpCosts"
      :night-count="nightCount"
      :pre-booking="house.onlineReservation && periodBookableOnline"
      :public-price="Number(selectedPeriod?.publicPrice)"
      :valid-price="!!selectedPeriod?.validPrice"
      data-testid="inquiry-date-picker__block-total"
    />

    <InquiryDatePickerSubmit
      v-if="!isMobile"
      :blocked-calendar="blockedCalendar"
      :booking-sent="bookingSent"
      :calendar-error="baseCalendarRef?.calendarErrors[0]"
      :online-reservation="house.onlineReservation"
      :period-bookable-online="periodBookableOnline"
      :show-total-price="showTotalPrice"
      @submit-date-picker="submitDatePicker"
    />

    <div
      :class="[
        {
          'pb-20': !house.onlineReservation,
          'pb-44': house.onlineReservation,
        },
        'mt-6 flex flex-col items-center border-t border-gray-200 pt-6 md:pb-0',
      ]"
    >
      <ClientOnly fallback-tag="p">
        <p class="mb-1 text-center text-md lg:text-base">
          {{
            t('inquiry.contact_us', {
              startTimeWeek,
              endTimeWeek,
              saturdayEndTimeWeek,
            })
          }}
          <BasePhoneNumber
            class="!text-secondary-400"
            @click="trackHeaderPhone"
          />
        </p>
      </ClientOnly>
    </div>

    <InquiryDatePickerSubmit
      v-if="isMobile"
      class="md:p-O fixed bottom-0 left-0 w-full bg-white p-4 md:static"
      :blocked-calendar="blockedCalendar"
      :booking-sent="bookingSent"
      :calendar-error="baseCalendarRef?.calendarErrors[0]"
      :online-reservation="false"
      :period-bookable-online="periodBookableOnline"
      :show-total-price="showTotalPrice"
      @submit-date-picker="submitDatePicker($event, isBooking)"
    />
  </div>
</template>

<style>
.modal.fade .modal-dialog.inquiry-block__modal--visible {
  @apply overflow-visible;
}

.inquiry-date-picker__base-calendar.disabled {
  cursor: pointer;
}

.inquiry-date-picker__calendar {
  margin: 0 0 1.5rem 0;
}

.inquiry-date-picker__calendar .datepicker__close-button {
  @apply hidden;
}

@media screen and (max-width: 767px) {
  .inquiry-date-picker__calendar .datepicker__months,
  .inquiry-date-picker__calendar .datepicker.show-tooltip .datepicker__months {
    height: calc(100% - 170px);
  }
}

.inquiry-date-picker__calendar .datepicker__wrapper {
  @apply !h-0;
}

.inquiry-date-picker__calendar label,
.inquiry-date-picker__calendar .datepicker__dummy-wrapper,
.inquiry-date-picker__calendar .datepicker__clear-button {
  @apply hidden;
}

@screen md {
  .inquiry-date-picker__calendar .datepicker__wrapper {
    @apply !h-[51px];
  }

  .inquiry-date-picker__calendar label {
    @apply inline-block;
  }

  .inquiry-date-picker__calendar .datepicker__dummy-wrapper {
    @apply flex;
  }

  .inquiry-date-picker__calendar .datepicker__clear-button {
    @apply block;
  }

  .inquiry-date-picker__base-calendar .datepicker__wrapper .datepicker {
    @apply left-0;
  }
}

@screen lg {
  .inquiry-date-picker__base-calendar .datepicker__wrapper .datepicker {
    @apply left-auto;
  }
}

.inquiry-date-picker__minimum-stay {
  margin: 0.25rem 0 0 0;
  @apply text-sm;
}

.inquiry-date-picker__minimum-stay--danger {
  @apply text-error;
}

.gray-500 {
  @apply text-gray-500;
}
</style>
